import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { UserInput } from "@/app/Users/Components/UserInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { assignAdminToWorkOrderMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { useUser } from "@/lib/Auth/useUser";

type AssignAdminFormValues = {
  user_id: string | null;
};

const validation = z.object({
  user_id: z.string().min(1, "Required"),
});

export const AssignAdminDialog: DialogComponent<
  { workOrderId: string; assigneeIds: string[] },
  { id: string }
> = ({ workOrderId, assigneeIds }) => {
  const { close, resolve } = useDialogState<{ id: string }>();
  const t = useTranslations("workOrder.details");
  const { mutateAsync } = useGqlMutation(assignAdminToWorkOrderMutation, {
    onError: () => toast.error(t("assignAdminFailed")),
  });
  const me = useUser();

  return (
    <BaseDialog title={t("assignAdmin")} subtitle={t("assignAdminSubtitle")}>
      <GenericForm<AssignAdminFormValues>
        // Whoever opened this is the likeliest assignee, unless they are on the
        // work order already - adding a second name means adding someone else.
        initialValues={{
          user_id: assigneeIds.includes(me.id) ? null : me.id,
        }}
        validationSchema={toFormikValidationSchema(validation)}
        onSubmit={async (values) => {
          const result = await mutateAsync({
            work_order_id: workOrderId,
            user_id: values.user_id!,
          }).catch(() => null);

          if (!result) {
            return;
          }

          await close();
          resolve?.({ id: result.assignAdminToWorkOrder.id });
        }}
      >
        <UserInput
          name="user_id"
          label={t("assignee")}
          excludeIds={assigneeIds}
        />
        <DialogSubmitButton text={t("assign")} />
      </GenericForm>
    </BaseDialog>
  );
};
